/**
 * Route Guard Hook
 * 
 * Handles permission checks for route access
 * Validates user can access requested view and admin sections
 */

import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { canAccessAdminSection } from '../admin/adminConfig'
import { getRouteForView, getRouteState } from './routeUtils'
import { ROUTE_PATHS } from './routes.config'

/**
 * Hook for protecting routes based on permissions and loading state
 * 
 * @param {Object} params - Guard parameters
 * @param {string} params.pathname - Current URL pathname
 * @param {string} params.accessRole - User's access role
 * @param {Array} params.visibleNav - Available navigation items for user
 * @param {boolean} params.isLoading - Whether profile/session is still loading
 * @param {boolean} params.isSignedIn - Whether user has active session
 * @returns {void}
 */
export const useRouteGuard = ({
  pathname,
  accessRole,
  visibleNav,
  sessionLoading,
  profileLoading,
  isSignedIn
}) => {
  const navigate = useNavigate()
  const prevSessionLoading = useRef(sessionLoading)
  const prevSignedIn = useRef(isSignedIn)

  useEffect(() => {
    // bail early when authentication state is just becoming valid. there are two
    // common situations where we want to postpone permission checks until the
    // profile subsystem has had a chance to load:
    //   * initial session verification (sessionLoading toggles false)
    //   * user just signed in (isSignedIn transitions false->true)
    // either scenario may leave `accessRole` at its default value while we fetch
    // the real role, so we skip a single guard invocation to avoid a premature
    // redirect.
    if ((prevSessionLoading.current && !sessionLoading) ||
        (!prevSignedIn.current && isSignedIn)) {
      prevSessionLoading.current = sessionLoading
      prevSignedIn.current = isSignedIn
      return
    }
    prevSessionLoading.current = sessionLoading
    prevSignedIn.current = isSignedIn

    const routeState = getRouteState(pathname)

    // Always handle legacy redirects and unknown paths immediately, even before auth
    if (routeState.kind === 'legacy_redirect') {
      navigate(routeState.redirectPath, { replace: true })
      return
    }

    if (routeState.kind === 'unknown') {
      navigate(routeState.redirectPath, { replace: true })
      return
    }

    // Normalize trailing slash or similar; this mirrors previous standalone effect
    if (routeState.normalizedPath !== pathname) {
      navigate(routeState.normalizedPath, { replace: true })
      return
    }

    const isLoading = sessionLoading || profileLoading

    // Guard access only when not loading and signed in
    if (isLoading || !isSignedIn) return

    const fallbackView = visibleNav[0]?.key || 'home'

    // Handle standard view routes with permission checks
    if (routeState.kind === 'view') {
      const requestedView = routeState.viewKey
      const canAccessRequestedView = visibleNav.some((item) => item.key === requestedView)

      if (!canAccessRequestedView) {
        navigate(getRouteForView(fallbackView), { replace: true })
        return
      }

      // If admin ops, check section permission
      if (requestedView === 'admin_ops' && routeState.adminSection) {
        if (!canAccessAdminSection(accessRole, routeState.adminSection)) {
          navigate(ROUTE_PATHS.admin_ops, { replace: true })
        }
      }
    }
  }, [pathname, accessRole, visibleNav, sessionLoading, profileLoading, isSignedIn, navigate])
}

/**
 * Hook for pathnames with trailing slash normalization
 */
export const usePathNormalization = (pathname) => {
  const navigate = useNavigate()

  useEffect(() => {
    const routeState = getRouteState(pathname)

    if (routeState.normalizedPath !== pathname) {
      navigate(routeState.normalizedPath, { replace: true })
    }
  }, [pathname, navigate])
}
